import {
  Dispatch,
  Fragment,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Grid,
  Box,
  Typography,
  Button,
  Link as MuiLink,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { FormatAbbrDateYear, FormatDate, formatLastUsed } from "~/lib/date";
import {
  GetApiTokensQuery,
  SERVICE_ACCOUNT_ASSET_COUNT,
  ServiceAccountForwardPaginationQuery,
} from "~/operations";
import { DetailRow } from "~/components/data-table";
import { APITokenToDelete } from "~/pages/space-settings/api-tokens/settings-api-tokens";
import { CanDeleteAgents } from "~/lib/iam/actions/mondoo/agents/AgentManager/DeleteAgents";
import { CanSetServiceAccountMembership } from "~/lib/iam/actions/mondoo/agents/AgentManager/SetServiceAccountMembership";
import { useScope } from "~/hooks/useScope";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";

export type ApiTokenConnection = NonNullable<GetApiTokensQuery["apiTokens"]>;
export type ApiTokenEdge = NonNullable<ApiTokenConnection["edges"]>[0];
export type ApiToken = NonNullable<ApiTokenEdge["node"]>;

export type ServiceAccountConnection = NonNullable<
  ServiceAccountForwardPaginationQuery["serviceAccounts"]
>;
export type ServiceAccountEdge = NonNullable<
  ServiceAccountConnection["edges"]
>[0];
export type ServiceAccount = NonNullable<ServiceAccountEdge["node"]>;

export type TokenTableRowProps = {
  edge: ApiTokenEdge | ServiceAccountEdge;
  setDeleteDialogOpen: Dispatch<SetStateAction<boolean>>;
  setItemToDelete: Dispatch<SetStateAction<APITokenToDelete | undefined>>;
  isSelectable: boolean;
  selection: APITokenToDelete[];
  setSelection: Dispatch<SetStateAction<APITokenToDelete[]>>;
  setEditDialogOpen: Dispatch<SetStateAction<boolean>>;
  handleEditPermissions: (token: ApiToken | ServiceAccount) => void;
};

const AGENT_MRN_LABEL = "agent-mrn";

export function TokenTableRow({
  edge,
  setDeleteDialogOpen,
  setItemToDelete,
  isSelectable,
  selection,
  setSelection,
  handleEditPermissions,
}: TokenTableRowProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const scope = useScope();
  const { isFeatureEnabled } = useFeatureFlags();
  const agentInfoEnabled = isFeatureEnabled("Agent Info");

  const handleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  const formatRoles = (
    roles: ApiToken["roles"] | ServiceAccount["roles"] | undefined,
  ) => {
    if (!roles || roles?.length === 0) return "No Permissions";
    // strip "Space" from the title
    const regex = new RegExp(/space/i);
    const rolesArray = roles.map((x) => {
      return x.title.replace(regex, "").trim();
    });
    return rolesArray.join(", ");
  };

  const formatCreatedBy = (
    creator: ApiToken["creator"] | ServiceAccount["creator"],
  ) => {
    if (!creator?.email) {
      return "";
    }

    return `Generated by ${creator?.email}, ${FormatDate(
      edge.node?.createdAt,
    )}`;
  };

  const handleDeleteBegin = (mrn: string | null | undefined) => {
    if (!mrn) return;

    setItemToDelete({
      mrn,
      date: edge.node?.createdAt
        ? FormatAbbrDateYear(edge.node?.createdAt)
        : null,
      name: edge.node?.creator?.email,
      reason: edge.node?.description,
    });
    setDeleteDialogOpen(true);
  };

  const handleCheck = (
    _e: SyntheticEvent,
    checked: boolean,
    mrn: string | undefined,
  ) => {
    if (!mrn) return;
    if (checked) {
      setSelection((prev) => [
        ...prev,
        {
          mrn,
          reason: edge.node?.description,
          date: FormatAbbrDateYear(edge.node?.createdAt),
          name: edge?.node?.creator?.email,
        },
      ]);
    } else {
      const selectedItemsCopy = [...selection];
      const found = selectedItemsCopy.findIndex((x) => x.mrn === mrn);
      selectedItemsCopy.splice(found, 1);
      setSelection(selectedItemsCopy);
    }
  };

  const isSelected = Boolean(selection.find((s) => s.mrn === edge?.node?.mrn));
  const className = isSelected ? "selected" : "";

  const agentMrn = useMemo(() => {
    if (edge.node?.__typename !== "ServiceAccount") return null;
    return (
      edge.node.labels?.find(({ key }) => key === AGENT_MRN_LABEL)?.value ??
      null
    );
  }, [edge.node]);

  const serviceAccountMrn = edge.node?.mrn;

  const [fetchAssetCount, { data: assetCountData, called: assetCountCalled }] =
    useLazyQuery(SERVICE_ACCOUNT_ASSET_COUNT, {
      fetchPolicy: "cache-first",
    });

  useEffect(() => {
    if (!isOpen || !agentInfoEnabled || !serviceAccountMrn || assetCountCalled)
      return;
    const scopeMrn = scope.spaceScope?.mrn;
    if (!scopeMrn) return;
    fetchAssetCount({
      variables: {
        scopeMrn,
        queryTerms: [
          JSON.stringify({
            "mondoo.com/agent-mrn": serviceAccountMrn,
          }),
        ],
        first: 1,
      },
    });
  }, [
    isOpen,
    agentInfoEnabled,
    serviceAccountMrn,
    scope.spaceScope?.mrn,
    fetchAssetCount,
    assetCountCalled,
  ]);

  const assetCount = assetCountData?.assets?.totalCount ?? 0;

  const agentLink = (() => {
    if (!agentInfoEnabled) return null;
    if (!agentMrn) return null;
    const agentId = agentMrn.split("/").pop();
    if (!agentId) return null;
    const spaceParams = scope.spaceScope?.params ?? "";
    const inventoryParams = new URLSearchParams(spaceParams);
    inventoryParams.set(
      "label",
      JSON.stringify({ "mondoo.com/agent-mrn": serviceAccountMrn }),
    );
    return (
      <>
        <Typography variant="caption" sx={{ display: "block" }}>
          <MuiLink
            component={Link}
            to={`/space/integrations/managed/${encodeURIComponent(agentId)}?${spaceParams}`}
          >
            View Managed Client Agent
          </MuiLink>
        </Typography>
        {assetCount > 0 && (
          <Typography variant="caption" sx={{ display: "block" }}>
            <MuiLink
              component={Link}
              to={`/space/inventory?${inventoryParams}`}
            >
              View Assets
            </MuiLink>
          </Typography>
        )}
      </>
    );
  })();

  return (
    <Fragment>
      <TableRow
        onClick={handleOpen}
        className={className + " token-table-row"}
        sx={{
          background: (theme) =>
            isOpen ? theme.palette.background.lighter : "unset",
        }}
      >
        {isSelectable && (
          <TableCell>
            <Checkbox
              checked={isSelected}
              onChange={(e, checked) =>
                handleCheck(e, checked, edge?.node?.mrn)
              }
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
        )}
        <TableCell>{edge?.node?.name}</TableCell>
        <TableCell>{formatRoles(edge.node?.roles)}</TableCell>
        <TableCell>{formatLastUsed(edge?.node?.createdAt)}</TableCell>
        <TableCell>{formatLastUsed(edge?.node?.lastUsed)}</TableCell>
        <TableCell align="right">
          <IconButton aria-label="expand query" size="small">
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      {/* Accordion Content Row */}
      <DetailRow colSpan={isSelectable ? 6 : 5} open={isOpen}>
        <Grid container py={1}>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
            size={{
              xs: 12,
              sm: "grow",
            }}
          >
            <Box>
              <Typography>{edge.node?.description}</Typography>
            </Box>
            {agentLink}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {formatCreatedBy(edge.node?.creator)}
            </Typography>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
            }}
            size={{
              xs: 12,
              sm: "auto",
            }}
          >
            <CanSetServiceAccountMembership>
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ mb: 1 }}
                onClick={() =>
                  handleEditPermissions(edge.node as ApiToken | ServiceAccount)
                }
              >
                Permissions
              </Button>
            </CanSetServiceAccountMembership>
            <CanDeleteAgents>
              <Button
                size="small"
                color="error"
                variant="outlined"
                endIcon={<DeleteIcon />}
                sx={{ mt: 1 }}
                onClick={() => handleDeleteBegin(edge?.node?.mrn)}
              >
                Delete
              </Button>
            </CanDeleteAgents>
          </Grid>
        </Grid>
      </DetailRow>
    </Fragment>
  );
}
